import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ChevronLeft, ChevronRight, ArrowLeft, ShoppingCart } from 'lucide-react';
import { WooCommerceDataProvider } from '../adapters/catalog/woocommerce';
import { useCart } from '../core/cart/CartContext';
import { formatPrice, isProductInStock, getStockStatusText, isPreOrder } from '../../utils/helpers';
import { Loading } from '../../components/ui/Loading';
import AddItemPopup from '../../components/AddItemPopup';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import type { Product } from '../core/ports';
import { VariationSelector } from '../ui/VariationSelector';
import { ProductCard } from '../ui/ProductCard';

export default function DetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart, getCartItemQuantity } = useCart();
  
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedVariation, setSelectedVariation] = useState<any | null>(null);
  const [upsellProducts, setUpsellProducts] = useState<Product[]>([]);

  useEffect(() => {
    // Auto-select default variation if provided by backend
    if (product && (product as any).variations && Array.isArray((product as any).variations)) {
      const defaults = (product as any).defaultAttributes || (product as any).default_attributes;
      if (defaults && Object.keys(defaults).length > 0) {
        const match = (product as any).variations.find((v: any) => {
          const attrs = v.attributes || {};
          return Object.keys(defaults).every((k) => {
            const val = defaults[k];
            return attrs[k] === val || attrs[`attribute_${k}`] === val || attrs[`pa_${k}`] === val || attrs[`attribute_pa_${k}`] === val;
          });
        });
        if (match) setSelectedVariation(match);
      }
    }
  }, [product]);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) {
        setError('No product ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const productData = await WooCommerceDataProvider.getProduct(Number(id));
        setProduct(productData);
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  // Fetch linked products (upsells)
  useEffect(() => {
    const fetchLinkedProducts = async () => {
      if (!id) return;
      
      // Check if getLinkedProducts is available
      if (!WooCommerceDataProvider.getLinkedProducts) {
        return;
      }

      try {
        const response = await WooCommerceDataProvider.getLinkedProducts(Number(id));
        if (response && response.upsells) {
          setUpsellProducts(response.upsells);
        }
      } catch (err) {
        console.error('Error fetching linked products:', err);
        // Silently fail - upsells are optional
      }
    };

    fetchLinkedProducts();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-primary text-tertiary">
        <Header />
        <div className="h-20" />
        <section className="gallery-carousel" style={{paddingTop: '120px', paddingBottom: '30px'}}>
          <div className="container">
            <div className="flex justify-center items-center py-32">
              <Loading size="lg" text="Loading product details..." />
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-primary text-tertiary">
        <Header />
        <div className="h-20" />
        <section className="gallery-carousel" style={{paddingTop: '120px', paddingBottom: '30px'}}>
          <div className="container">
            <h1 className="h2 section-title" style={{marginBottom: '40px'}}>Product Not Found</h1>
            <div className="text-center">
              <p className="about-text" style={{marginBottom: '30px'}}>{error || 'The product you are looking for does not exist.'}</p>
              <button onClick={() => navigate('/shop')} className="btn btn-primary">
                <ArrowLeft size={18} />
                <span>Back to Shop</span>
              </button>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  // Calculate remaining stock by subtracting cart quantity
  const cartQuantity = getCartItemQuantity(product.id);
  const remainingStock = product.stockQuantity !== undefined ? product.stockQuantity - cartQuantity : undefined;
  
  const preOrder = isPreOrder(product);
  const inStock = isProductInStock(product.stockStatus, remainingStock);
  // Pre-orders are purchasable even when stock has not arrived yet
  const canPurchase = preOrder || inStock;
  const isVariable = ((product as any).variations && Array.isArray((product as any).variations) && (product as any).variations.length > 0) || product.type === 'variable';
  
  // For variable products, calculate remaining stock for selected variation
  const selectedCartQuantity = selectedVariation ? getCartItemQuantity(selectedVariation.id) : 0;
  const selectedRemainingStock = selectedVariation && selectedVariation.stockQuantity !== undefined 
    ? selectedVariation.stockQuantity - selectedCartQuantity 
    : undefined;
  const selectedInStock = selectedVariation ? isProductInStock(selectedVariation.stockStatus, selectedRemainingStock) : true;
  // A pre-order tag applies to the whole product, variations included
  const canPurchaseSelected = preOrder || selectedInStock;
  const hasMultipleImages = product.images && product.images.length > 1;
  const selectedVarImage = (selectedVariation as any)?.image as string | undefined;
  const mainImageSrc = selectedVarImage || product.images[currentImageIndex] || '/assets/placeholder-product.jpg';

  const priceOnSale = selectedVariation ? !!(selectedVariation.onSale && selectedVariation.salePrice) : !!(product.onSale && product.salePrice);
  const priceValue = selectedVariation ? Number(selectedVariation.price || 0) : Number(product.price || 0);
  const regularValue = selectedVariation ? Number(selectedVariation.regularPrice || priceValue) : Number(product.regularPrice || product.price || 0);

  const handleAdd = async () => {
    if (!canPurchase) return;
    setAdding(true);
    try {
      // For variable products require a selected variation
      if (isVariable) {
        if (!selectedVariation) { setAdding(false); return; }
        // Pass variation data to addToCart
        await addToCart(
          product, 
          1, 
          selectedVariation.id, 
          selectedVariation.displayName || selectedVariation.name,
          undefined, // bundleSelections
          selectedVariation.attributes // variationAttributes
        );
      } else {
        await addToCart(product, 1);
      }
      setPopupOpen(true);
    } finally {
      setAdding(false);
    }
  };

  const nextImage = () => {
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
      setImageLoading(true);
    }
  };

  const previousImage = () => {
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
      setImageLoading(true);
    }
  };

  const selectImage = (index: number) => {
    setCurrentImageIndex(index);
    setImageLoading(true);
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
  };

  return (
    <div className="min-h-screen bg-primary text-tertiary">
      <Helmet>
        <title>{product ? `${product.name} - Pokemon & One Piece Cards | Oracle Gaming` : 'Product Details | Oracle Gaming'}</title>
        <meta name="description" content={product ? `Buy ${product.name} online at Oracle Gaming. ${product.shortDescription || 'High-quality trading card game products with fast shipping across South Africa.'}` : 'Shop Pokemon and One Piece cards online at Oracle Gaming South Africa.'} />
        <meta name="keywords" content={product ? `${product.name}, Pokemon cards, One Piece cards, TCG South Africa, ${product.categories?.join(', ') || ''}, trading cards online` : 'Pokemon cards, One Piece cards, TCG South Africa, trading cards online'} />
        <meta property="og:title" content={product ? `${product.name} - Pokemon & One Piece Cards | Oracle Gaming` : 'Product Details | Oracle Gaming'} />
        <meta property="og:description" content={product ? `Buy ${product.name} online at Oracle Gaming. ${product.shortDescription || 'High-quality trading card game products with fast shipping across South Africa.'}` : 'Shop Pokemon and One Piece cards online at Oracle Gaming South Africa.'} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={`https://oracle-gaming.vercel.app/product/${product?.id}`} />
        <meta property="og:image" content={product?.images?.[0] || 'https://oracle-gaming.vercel.app/images/logo.svg'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={product ? `${product.name} - Pokemon & One Piece Cards | Oracle Gaming` : 'Product Details | Oracle Gaming'} />
        <meta name="twitter:description" content={product ? `Buy ${product.name} online at Oracle Gaming. ${product.shortDescription || 'High-quality trading card game products with fast shipping across South Africa.'}` : 'Shop Pokemon and One Piece cards online at Oracle Gaming South Africa.'} />
        <meta name="twitter:image" content={product?.images?.[0] || 'https://oracle-gaming.vercel.app/images/logo.svg'} />
        <link rel="canonical" href={`https://oracle-gaming.vercel.app/product/${product?.id}`} />
        {product && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              "name": product.name,
              "description": product.shortDescription || product.description,
              "image": product.images || [],
              "brand": product.brand || "Oracle Gaming",
              "offers": {
                "@type": "Offer",
                "price": product.salePrice || product.regularPrice,
                "priceCurrency": "ZAR",
                "availability": product.stockStatus === 'instock' ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                "seller": {
                  "@type": "Store",
                  "name": "Oracle Gaming"
                }
              }
            })}
          </script>
        )}
      </Helmet>
      <Header />
      <div className="h-20" />
      
      {/* Hero Section */}
      <section className="gallery-carousel" style={{paddingTop: '120px', paddingBottom: '30px'}}>
        <div className="w-full px-8">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-tertiary hover:text-white transition-colors"
              style={{ fontSize: '16px' }}
            >
              <ArrowLeft size={20} />
              <span>Back</span>
            </button>
          </div>
        </div>
      </section>

      {/* Product Details Section */}
      <section style={{paddingTop: 0, paddingBottom: '80px', backgroundColor: '#1b1c21'}}>
        <div className="w-full px-8">
          <div style={{marginBottom: '40px', maxWidth: 'none'}}>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 items-start" style={{maxWidth: 'none'}}>
              {/* Left Side - Images */}
              <div className="space-y-6">
                {/* Main Image */}
                <div className="relative w-full h-64 sm:h-96 lg:h-[350px] xl:h-[400px] bg-gray-800 overflow-hidden rounded-lg">
                  {imageLoading && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loading size="lg" />
                    </div>
                  )}
                  
                  <img 
                    src={mainImageSrc} 
                    alt={`${product.name} - ${currentImageIndex + 1}`}
                    className={`w-full h-full object-contain transition-opacity duration-300 ${
                      imageLoading ? 'opacity-0' : 'opacity-100'
                    }`} 
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                  />

                  {/* Navigation Arrows */}
                  {hasMultipleImages && (
                    <>
                      <button
                        onClick={previousImage}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110 rounded"
                        title="Previous Image"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      
                      <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110 rounded"
                        title="Next Image"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>

                      {/* Image Counter */}
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-sm px-3 py-1 rounded">
                        {currentImageIndex + 1} / {product.images.length}
                      </div>
                    </>
                  )}
                </div>

                {/* Feature Images (Smaller) */}
                {hasMultipleImages && (
                  <div className="grid grid-cols-6 sm:grid-cols-8 lg:grid-cols-6 xl:grid-cols-8 gap-2">
                    {product.images.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => selectImage(index)}
                        className={`aspect-square overflow-hidden border-2 transition-all duration-200 rounded bg-gray-800 ${
                          index === currentImageIndex
                            ? 'border-orange shadow-lg'
                            : 'border-gray-600 hover:border-gray-400'
                        }`}
                      >
                        <img
                          src={image}
                          alt={`${product.name} thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Right Side - Product Information */}
              <div className="space-y-8 xl:col-span-2" style={{paddingLeft: '2rem'}}>
                {/* 1. Name (h3) */}
                <p className="h3" style={{color: 'var(--white)', marginBottom: '20px', fontSize: 'var(--fs-2)'}}>
                  {product.name}
                </p>

                {/* 2. Brand and Category */}
                <div className="flex flex-wrap items-center gap-3 mb-6">
                  {product.brand && (
                    <span className="text-sm text-white px-4 py-2 font-bold uppercase">
                      {product.brand}
                    </span>
                  )}
                  {product.brand && product.categories?.length > 0 && (
                    <span className="text-sm text-gray-400">•</span>
                  )}
                  {product.categories?.slice(0, 3).map((cat, i) => (
                    <span key={i} className="text-sm text-white px-4 py-2 font-medium uppercase">
                      {cat}
                    </span>
                  ))}
                </div>

                {/* 3. Price */}
                <div className="mb-4">
                  {priceOnSale ? (
                    <div className="flex items-center gap-3">
                      <span className="text-2xl sm:text-3xl font-bold" style={{color: 'var(--orange)'}}>
                        {formatPrice(priceValue)}
                      </span>
                      <span className="text-base sm:text-lg text-gray-400 line-through">
                        {formatPrice(regularValue)}
                      </span>
                      <span className="text-xs bg-red-500 text-white px-2 py-1 font-bold rounded">
                        SALE
                      </span>
                    </div>
                  ) : (
                    <span className="text-2xl sm:text-3xl font-bold" style={{color: 'var(--orange)'}}>
                      {formatPrice(priceValue)}
                    </span>
                  )}
                </div>

                {/* 4. Stock Level */}
                <div className="about-text mb-4" style={{fontSize: 'var(--fs-8)'}}>
                  {preOrder ? (
                    <span style={{ color: 'var(--tertiary)' }}>
                      Available to pre-order &mdash; ships when stock arrives
                    </span>
                  ) : isVariable && selectedVariation ? (
                    <span className={selectedInStock ? 'text-green-400' : 'text-red-400'}>
                      {selectedRemainingStock !== undefined 
                        ? `${selectedRemainingStock} in stock` 
                        : getStockStatusText(selectedVariation.stockStatus, selectedVariation.stockQuantity)
                      }
                    </span>
                  ) : (
                    <span className={inStock ? 'text-green-400' : 'text-red-400'}>
                      {remainingStock !== undefined 
                        ? `${remainingStock} in stock` 
                        : getStockStatusText(product.stockStatus, product.stockQuantity)
                      }
                    </span>
                  )}
                </div>

                {/* 5. Variations / Add to Cart */}
                {isVariable ? (
                  <div className="mb-6">
                    <VariationSelector 
                      variations={(product as any).variations}
                      variationAttributes={(product as any).variationAttributes}
                      onVariationSelect={(v) => setSelectedVariation(v)}
                      selectedVariation={selectedVariation || undefined}
                    />
                  </div>
                ) : null}
                <div className="mb-10">
                  <div className="flex gap-4">
                    <button
                      onClick={handleAdd}
                      disabled={isVariable ? (!selectedVariation || !canPurchaseSelected || adding) : (!canPurchase || adding)}
                      className={`btn btn-primary flex-1 py-4 px-6 text-lg flex items-center justify-center gap-3 transition-all ${
                        isVariable ? (selectedVariation && canPurchaseSelected ? 'hover:scale-105' : 'opacity-50 cursor-not-allowed') : (canPurchase 
                          ? 'hover:scale-105' 
                          : 'opacity-50 cursor-not-allowed')
                      }`}
                    >
                      <ShoppingCart size={20} />
                      <span>{adding ? 'Adding...' : (isVariable ? (selectedVariation ? (canPurchaseSelected ? (preOrder ? 'Pre Order Selected Option' : 'Add Selected Option') : 'Out of Stock') : 'Select an option') : (canPurchase ? (preOrder ? 'Pre Order' : 'Add to Cart') : 'Out of Stock'))}</span>
                    </button>
                    
                    <button
                      onClick={() => navigate('/cart')}
                      className="btn w-1/2 py-4 px-6 text-lg flex items-center justify-center gap-3 transition-all border-2 border-tertiary text-white hover:text-tertiary hover:scale-105"
                    >
                      <ShoppingCart size={20} />
                      <span>View Cart</span>
                    </button>
                  </div>
                </div>

                {/* 6/7 moved below for full-width layout */}
              </div>
            </div>
          </div>

          {/* Back to Shop link removed by request */}
        </div>
      </section>

      {/* Description Section (full width under everything) */}
      {(product.shortDescription || product.description) && (
        <section style={{ padding: '20px 0 40px 0', backgroundColor: '#1b1c21' }}>
          <div className="w-full px-8" style={{ maxWidth: 1200, margin: '0 auto' }}>
            {product.shortDescription && (
              <div className="mb-8">
                <h4 className="about-subtitle" style={{ marginBottom: '15px', fontSize: 'var(--fs-6)' }}>
                  Product Overview
                </h4>
                <p className="about-text" style={{ fontSize: 'var(--fs-8)', lineHeight: '1.6' }}>
                  {product.shortDescription}
                </p>
              </div>
            )}
            {product.description && (
              <div>
                <h4 className="about-subtitle" style={{ marginBottom: '15px', fontSize: 'var(--fs-6)' }}>
                  Product Description
                </h4>
                <style>{`
                  [data-product-desc] h1,
                  [data-product-desc] h2,
                  [data-product-desc] h3,
                  [data-product-desc] h4,
                  [data-product-desc] h5,
                  [data-product-desc] h6 { color: var(--orange) !important; }
                  [data-product-desc] p,
                  [data-product-desc] li,
                  [data-product-desc] span { color: var(--light-gray) !important; }
                  [data-product-desc] a { color: var(--orange) !important; text-decoration: underline; }
                `}</style>
                <div
                  data-product-desc
                  className="about-text"
                  style={{ fontSize: 'var(--fs-8)', lineHeight: '1.6' }}
                  dangerouslySetInnerHTML={{ __html: product.description }}
                />
              </div>
            )}
          </div>
        </section>
      )}

      {/* You May Also Like Section - Upsell Products */}
      {upsellProducts.length > 0 && (
        <section className="section gears" style={{ padding: '40px 0 80px 0', backgroundColor: '#1b1c21' }}>
          <div className="container">
            <h2 className="h2 section-title" style={{ marginBottom: '40px', textAlign: 'center' }}>
              You May Also Like
            </h2>
            <ul className="gears-list" style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
              gap: '30px',
              listStyle: 'none',
              padding: 0,
              margin: 0
            }}>
              {upsellProducts.map((upsellProduct) => (
                <li key={upsellProduct.id}>
                  <ProductCard 
                    product={upsellProduct} 
                    onViewDetails={(p) => navigate(`/shop/product/${p.id}`)}
                  />
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Slide-in confirmation popup */}
      <AddItemPopup isOpen={popupOpen} onClose={() => setPopupOpen(false)} message={product.name} />
      
      <Footer />
    </div>
  );
}